import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { Modal } from "../../components/ui/Modal";
import { Input } from "../../components/ui/Input";
import { useApi } from "../../context/ApiContext";
import SearchableDropdown from "../../components/SearchableDropdown";

interface Location {
  _id: string;
  country: string;
  countryCode: string;
  currency: string;
  currencySymbol: string;
  isActive: boolean;
}

interface LocationPricing {
  locationId: string;
  price?: number;
  inventoryQuantity?: number;
  isAvailable: boolean;
}

interface Product {
  _id: string;
  title: string;
  basePrice: number;
  inventoryQuantity?: number;
  variantOptions?: Array<{
    name: string;
    values: Array<{ value: string }>;
  }>;
  variantCombinations?: Array<{
    combination: string;
    price: number;
    inventoryQuantity?: number;
    locationPricing?: LocationPricing[];
  }>;
  locationPricing?: LocationPricing[];
}

interface RowData {
  productId: string;
  productTitle: string;
  combination?: string;
  combinationDisplay?: string;
  basePrice: number;
  baseQuantity?: number;
  locationPrice?: number;
  locationQuantity?: number;
  locationAvailable: boolean;
  originalLocationPrice?: number;
  originalLocationQuantity?: number;
  originalLocationAvailable: boolean;
}

interface CellPosition {
  rowIndex: number;
  colIndex: number; // 0: price, 1: quantity, 2: available
}

interface Change {
  productId: string;
  combination?: string;
  locationId: string;
  price?: number;
  inventoryQuantity?: number;
  isAvailable?: boolean;
}

export const BulkLocationManagement: React.FC = () => {
  const { api } = useApi();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rowData, setRowData] = useState<RowData[]>([]);
  const [focusedCell, setFocusedCell] = useState<CellPosition | null>(null);
  const [editingCell, setEditingCell] = useState<CellPosition | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [changes, setChanges] = useState<Map<string, Change>>(new Map());
  const [showSummary, setShowSummary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const tableRef = useRef<HTMLTableElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.get("/locations/admin?isActive=true");
        setLocations(response.data.locations || []);
      } catch (err: any) {
        setError(err.response?.data?.error || t("admin.inventory.errors.fetchLocations"));
      }
    };
    fetchLocations();
  }, [api]);

  // Fetch products when location is selected
  useEffect(() => {
    if (!selectedLocationId) {
      setProducts([]);
      setRowData([]);
      setLoading(false);
      return;
    }

    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get("/products?status=&limit=1000");
        const fetchedProducts = response.data.products || [];
        setProducts(fetchedProducts);

        // Build row data
        const rows: RowData[] = [];
        for (const product of fetchedProducts) {
          const locationPricing = product.locationPricing?.find(
            (lp: LocationPricing) => lp.locationId === selectedLocationId
          );

          if (!product.variantOptions || product.variantOptions.length === 0) {
            // Simple product without variants
            rows.push({
              productId: product._id,
              productTitle: product.title,
              basePrice: product.basePrice,
              baseQuantity: product.inventoryQuantity,
              locationPrice: locationPricing?.price,
              locationQuantity: locationPricing?.inventoryQuantity,
              locationAvailable: locationPricing?.isAvailable ?? true,
              originalLocationPrice: locationPricing?.price,
              originalLocationQuantity: locationPricing?.inventoryQuantity,
              originalLocationAvailable: locationPricing?.isAvailable ?? true,
            });
          } else {
            // Product with variants - create row for each combination
            if (product.variantCombinations && product.variantCombinations.length > 0) {
              for (const combo of product.variantCombinations) {
                const comboLocationPricing = combo.locationPricing?.find(
                  (lp: LocationPricing) => lp.locationId === selectedLocationId
                );
                const comboDisplay = combo.combination
                  .split("|")
                  .map((pair: string) => {
                    const [opt, val] = pair.split(":");
                    return `${opt}: ${val}`;
                  })
                  .join(", ");

                rows.push({
                  productId: product._id,
                  productTitle: product.title,
                  combination: combo.combination,
                  combinationDisplay: comboDisplay,
                  basePrice: combo.price,
                  baseQuantity: combo.inventoryQuantity,
                  locationPrice: comboLocationPricing?.price,
                  locationQuantity: comboLocationPricing?.inventoryQuantity,
                  locationAvailable: comboLocationPricing?.isAvailable ?? true,
                  originalLocationPrice: comboLocationPricing?.price,
                  originalLocationQuantity: comboLocationPricing?.inventoryQuantity,
                  originalLocationAvailable: comboLocationPricing?.isAvailable ?? true,
                });
              }
            }
          }
        }
        setRowData(rows);
      } catch (err: any) {
        setError(err.response?.data?.error || t("admin.inventory.errors.fetchProducts"));
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [selectedLocationId, api]);

  // Focus input when editing
  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  // Check for inconsistent inventory configuration
  const hasInconsistentInventory = useCallback(
    (rowIndex: number): boolean => {
      const row = rowData[rowIndex];
      if (!row) return false;

      // Find the product
      const product = products.find((p) => p._id === row.productId);
      if (!product) return false;

      // Check if this is a variant combination or base product
      if (row.combination) {
        // Variant combination - check if some locations have inventory but not all
        const combo = product.variantCombinations?.find(
          (c) => c.combination === row.combination
        );
        if (!combo || !combo.locationPricing) return false;

        const locationsWithInventory = combo.locationPricing.filter(
          (lp) => lp.inventoryQuantity !== undefined && lp.inventoryQuantity !== null
        );
        // Inconsistent if some locations have inventory but not all
        return locationsWithInventory.length > 0 && locationsWithInventory.length < locations.length;
      } else {
        // Base product - check if some locations have inventory but not all
        if (!product.locationPricing) return false;

        const locationsWithInventory = product.locationPricing.filter(
          (lp) => lp.inventoryQuantity !== undefined && lp.inventoryQuantity !== null
        );
        // Inconsistent if some locations have inventory but not all
        return locationsWithInventory.length > 0 && locationsWithInventory.length < locations.length;
      }
    },
    [rowData, products, locations]
  );

  // Check if a cell has been changed
  const isCellChanged = useCallback(
    (rowIndex: number, colIndex: number): boolean => {
      const row = rowData[rowIndex];
      if (!row) return false;

      const changeKey = `${row.productId}-${row.combination || "base"}-${selectedLocationId}`;
      const change = changes.get(changeKey);

      if (colIndex === 0) {
        // Price
        return change?.price !== undefined && change.price !== row.originalLocationPrice;
      } else if (colIndex === 1) {
        // Quantity
        return (
          change?.inventoryQuantity !== undefined &&
          change.inventoryQuantity !== row.originalLocationQuantity
        );
      } else if (colIndex === 2) {
        // Available
        return (
          change?.isAvailable !== undefined &&
          change.isAvailable !== row.originalLocationAvailable
        );
      }
      return false;
    },
    [rowData, changes, selectedLocationId]
  );

  // Get current cell value
  const getCellValue = useCallback(
    (rowIndex: number, colIndex: number): string | boolean => {
      const row = rowData[rowIndex];
      if (!row) return "";

      const changeKey = `${row.productId}-${row.combination || "base"}-${selectedLocationId}`;
      const change = changes.get(changeKey);

      if (colIndex === 0) {
        // Price
        return change?.price !== undefined
          ? change.price.toString()
          : row.locationPrice !== undefined
          ? row.locationPrice.toString()
          : "";
      } else if (colIndex === 1) {
        // Quantity
        return change?.inventoryQuantity !== undefined
          ? change.inventoryQuantity.toString()
          : row.locationQuantity !== undefined
          ? row.locationQuantity.toString()
          : "";
      } else if (colIndex === 2) {
        // Available
        return change?.isAvailable !== undefined
          ? change.isAvailable
          : row.locationAvailable;
      }
      return "";
    },
    [rowData, changes, selectedLocationId]
  );

  // Update change
  const updateChange = useCallback(
    (rowIndex: number, colIndex: number, value: string | boolean) => {
      const row = rowData[rowIndex];
      if (!row) return;

      const changeKey = `${row.productId}-${row.combination || "base"}-${selectedLocationId}`;
      const existingChange = changes.get(changeKey) || {
        productId: row.productId,
        combination: row.combination,
        locationId: selectedLocationId,
      };

      const newChange: Change = { ...existingChange };

      if (colIndex === 0) {
        // Price
        const numValue = value === "" ? undefined : parseFloat(value as string);
        if (numValue === row.originalLocationPrice) {
          delete newChange.price;
        } else {
          newChange.price = numValue;
        }
      } else if (colIndex === 1) {
        // Quantity
        const numValue = value === "" ? undefined : parseInt(value as string, 10);
        if (numValue === row.originalLocationQuantity) {
          delete newChange.inventoryQuantity;
        } else {
          newChange.inventoryQuantity = numValue;
        }
      } else if (colIndex === 2) {
        // Available
        const boolValue = value as boolean;
        if (boolValue === row.originalLocationAvailable) {
          delete newChange.isAvailable;
        } else {
          newChange.isAvailable = boolValue;
        }
      }

      // Remove change if it's back to original
      if (
        newChange.price === undefined &&
        newChange.inventoryQuantity === undefined &&
        newChange.isAvailable === undefined
      ) {
        const newChanges = new Map(changes);
        newChanges.delete(changeKey);
        setChanges(newChanges);
      } else {
        const newChanges = new Map(changes);
        newChanges.set(changeKey, newChange);
        setChanges(newChanges);
      }

      // Update row data for display
      setRowData((prev) => {
        const newRows = [...prev];
        const updatedRow = { ...newRows[rowIndex] };
        if (colIndex === 0) {
          updatedRow.locationPrice =
            newChange.price !== undefined ? newChange.price : row.originalLocationPrice;
        } else if (colIndex === 1) {
          updatedRow.locationQuantity =
            newChange.inventoryQuantity !== undefined
              ? newChange.inventoryQuantity
              : row.originalLocationQuantity;
        } else if (colIndex === 2) {
          updatedRow.locationAvailable =
            newChange.isAvailable !== undefined
              ? newChange.isAvailable
              : row.originalLocationAvailable;
        }
        newRows[rowIndex] = updatedRow;
        return newRows;
      });
    },
    [rowData, changes, selectedLocationId]
  );

  // Start editing cell
  const startEditing = useCallback(
    (rowIndex: number, colIndex: number) => {
      setEditingCell({ rowIndex, colIndex });
      const value = getCellValue(rowIndex, colIndex);
      setEditValue(colIndex === 2 ? (value ? "true" : "false") : (value as string));
    },
    [getCellValue]
  );

  // Finish editing
  const finishEditing = useCallback(
    (save: boolean = true) => {
      if (!editingCell || !save) {
        setEditingCell(null);
        setEditValue("");
        return;
      }

      const { rowIndex, colIndex } = editingCell;
      if (colIndex === 2) {
        // Boolean checkbox
        updateChange(rowIndex, colIndex, editValue === "true");
      } else {
        // Number input
        updateChange(rowIndex, colIndex, editValue);
      }

      setEditingCell(null);
      setEditValue("");
    },
    [editingCell, editValue, updateChange]
  );

  // Set initial focus when data loads
  useEffect(() => {
    if (rowData.length > 0 && !focusedCell) {
      setFocusedCell({ rowIndex: 0, colIndex: 0 });
      // Focus the table container to enable keyboard navigation
      if (tableContainerRef.current) {
        tableContainerRef.current.focus();
      }
    }
  }, [rowData.length, focusedCell]);

  // Focus table container when location is selected and data is loaded
  useEffect(() => {
    if (selectedLocationId && !loading && rowData.length > 0 && tableContainerRef.current) {
      tableContainerRef.current.focus();
    }
  }, [selectedLocationId, loading, rowData.length]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (rowData.length === 0) return;

      // Initialize focus if not set
      if (!focusedCell) {
        setFocusedCell({ rowIndex: 0, colIndex: 0 });
        return;
      }

      const { rowIndex, colIndex } = focusedCell;
      let newRowIndex = rowIndex;
      let newColIndex = colIndex;

      if (editingCell) {
        // When editing, handle Enter and Escape
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          finishEditing(true);
          // Move to next row, same column
          newRowIndex = Math.min(rowIndex + 1, rowData.length - 1);
        } else if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          finishEditing(false);
          return;
        } else if (e.key === "Tab") {
          e.preventDefault();
          e.stopPropagation();
          finishEditing(true);
          // Move to next cell
          if (e.shiftKey) {
            // Shift+Tab: move left
            if (colIndex > 0) {
              newColIndex = colIndex - 1;
            } else if (rowIndex > 0) {
              newRowIndex = rowIndex - 1;
              newColIndex = 2; // Last column
            }
          } else {
            // Tab: move right
            if (colIndex < 2) {
              newColIndex = colIndex + 1;
            } else if (rowIndex < rowData.length - 1) {
              newRowIndex = rowIndex + 1;
              newColIndex = 0; // First column
            }
          }
        } else {
          return; // Let input handle other keys
        }
      } else {
        // Navigation mode
        if (e.key === "ArrowUp") {
          e.preventDefault();
          e.stopPropagation();
          newRowIndex = Math.max(0, rowIndex - 1);
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          e.stopPropagation();
          newRowIndex = Math.min(rowData.length - 1, rowIndex + 1);
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          e.stopPropagation();
          newColIndex = Math.max(0, colIndex - 1);
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          e.stopPropagation();
          newColIndex = Math.min(2, colIndex + 1);
        } else if (e.key === "Enter" || (e.key === " " && colIndex !== 2)) {
          e.preventDefault();
          e.stopPropagation();
          startEditing(rowIndex, colIndex);
          return;
        } else if (e.key === "Home") {
          e.preventDefault();
          e.stopPropagation();
          if (e.ctrlKey || e.metaKey) {
            // Ctrl+Home: go to first row, first column
            newRowIndex = 0;
            newColIndex = 0;
          } else {
            // Home: go to first column
            newColIndex = 0;
          }
        } else if (e.key === "End") {
          e.preventDefault();
          e.stopPropagation();
          if (e.ctrlKey || e.metaKey) {
            // Ctrl+End: go to last row, last column
            newRowIndex = rowData.length - 1;
            newColIndex = 2;
          } else {
            // End: go to last column
            newColIndex = 2;
          }
        } else if (e.key === "PageUp") {
          e.preventDefault();
          e.stopPropagation();
          newRowIndex = 0;
        } else if (e.key === "PageDown") {
          e.preventDefault();
          e.stopPropagation();
          newRowIndex = rowData.length - 1;
        } else {
          return; // Not a navigation key
        }
      }

      setFocusedCell({ rowIndex: newRowIndex, colIndex: newColIndex });
      // Refocus container after navigation to keep keyboard events working
      setTimeout(() => {
        if (tableContainerRef.current && !editingCell) {
          tableContainerRef.current.focus();
        }
      }, 0);
    },
    [focusedCell, editingCell, rowData.length, startEditing, finishEditing]
  );

  // Get summary of changes
  const getChangesSummary = useMemo(() => {
    const summary: Change[] = Array.from(changes.values());
    return {
      total: summary.length,
      changes: summary,
    };
  }, [changes]);

  // Submit changes
  const handleSubmit = useCallback(async () => {
    if (changes.size === 0) {
      setError(t("admin.inventory.errors.noChanges"));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const updates: Change[] = Array.from(changes.values());
      const response = await api.post("/products/bulk/location-pricing", { updates });

      // Reset changes and reload data
      setChanges(new Map());
      setShowSummary(false);
      
      // Reload products
      const productsResponse = await api.get("/products?status=&limit=1000");
      const fetchedProducts = productsResponse.data.products || [];
      setProducts(fetchedProducts);

      // Rebuild row data
      const rows: RowData[] = [];
      for (const product of fetchedProducts) {
        const locationPricing = product.locationPricing?.find(
          (lp: LocationPricing) => lp.locationId === selectedLocationId
        );

        if (!product.variantOptions || product.variantOptions.length === 0) {
          rows.push({
            productId: product._id,
            productTitle: product.title,
            basePrice: product.basePrice,
            baseQuantity: product.inventoryQuantity,
            locationPrice: locationPricing?.price,
            locationQuantity: locationPricing?.inventoryQuantity,
            locationAvailable: locationPricing?.isAvailable ?? true,
            originalLocationPrice: locationPricing?.price,
            originalLocationQuantity: locationPricing?.inventoryQuantity,
            originalLocationAvailable: locationPricing?.isAvailable ?? true,
          });
        } else {
          if (product.variantCombinations && product.variantCombinations.length > 0) {
            for (const combo of product.variantCombinations) {
              const comboLocationPricing = combo.locationPricing?.find(
                (lp: LocationPricing) => lp.locationId === selectedLocationId
              );
              const comboDisplay = combo.combination
                .split("|")
                .map((pair: string) => {
                  const [opt, val] = pair.split(":");
                  return `${opt}: ${val}`;
                })
                .join(", ");

              rows.push({
                productId: product._id,
                productTitle: product.title,
                combination: combo.combination,
                combinationDisplay: comboDisplay,
                basePrice: combo.price,
                baseQuantity: combo.inventoryQuantity,
                locationPrice: comboLocationPricing?.price,
                locationQuantity: comboLocationPricing?.inventoryQuantity,
                locationAvailable: comboLocationPricing?.isAvailable ?? true,
                originalLocationPrice: comboLocationPricing?.price,
                originalLocationQuantity: comboLocationPricing?.inventoryQuantity,
                originalLocationAvailable: comboLocationPricing?.isAvailable ?? true,
              });
            }
          }
        }
      }
      setRowData(rows);

      // Show success message
      alert(t(`admin.inventory.success.updated_${response.data.modifiedCount === 1 ? "one" : "other"}`, { count: response.data.modifiedCount }));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.inventory.errors.saveChanges"));
    } finally {
      setSubmitting(false);
    }
  }, [changes, api, selectedLocationId]);

  const selectedLocation = locations.find((l) => l._id === selectedLocationId);

  return (
    <Container>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Heading>{t("admin.inventory.title")}</Heading>
          <Button variant="outline" onClick={() => navigate("/admin/products")}>
            {t("admin.inventory.backToProducts")}
          </Button>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        <Card>
          <div className="space-y-4 p-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("admin.inventory.selectCountry")}
              </label>
              <SearchableDropdown
                fetchOptions={async (search: string) => {
                  const filtered = locations
                    .filter((loc) =>
                      loc.country.toLowerCase().includes(search.toLowerCase())
                    )
                    .map((loc) => ({
                      _id: loc._id,
                      label: `${loc.country} (${loc.countryCode}) - ${loc.currency}`,
                    }));
                  return filtered;
                }}
                placeholder={t("admin.inventory.selectCountryPlaceholder")}
                value={selectedLocationId}
                getDisplayLabel={(val: string) => {
                  const loc = locations.find((l) => l._id === val);
                  return loc
                    ? `${loc.country} (${loc.countryCode}) - ${loc.currency}`
                    : "";
                }}
                onSelect={(option) => setSelectedLocationId(option._id)}
              />
            </div>

            {selectedLocationId && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      {t("admin.inventory.itemsToManage", { count: rowData.length })}
                    </p>
                    {changes.size > 0 && (
                      <p className="text-sm text-blue-600 font-medium">
                        {t("admin.inventory.changesPending", { count: changes.size })}
                      </p>
                    )}
                  </div>
                  {changes.size > 0 && (
                    <Button
                      onClick={() => setShowSummary(true)}
                      disabled={submitting}
                    >
                      {t("admin.inventory.reviewSubmit", { count: changes.size })}
                    </Button>
                  )}
                </div>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="lg" />
                  </div>
                ) : (
                  <div
                    ref={tableContainerRef}
                    className="border border-gray-200 rounded-lg overflow-auto max-h-[600px] outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onKeyDown={handleKeyDown}
                    tabIndex={0}
                    onClick={(e) => {
                      // Focus container when clicking on it
                      if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === "TD") {
                        tableContainerRef.current?.focus();
                      }
                    }}
                  >
                    <table
                      ref={tableRef}
                      className="min-w-full divide-y divide-gray-200"
                    >
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-64">
                            {t("admin.inventory.product")}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-48">
                            {t("admin.inventory.variant")}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                            {t("admin.inventory.basePrice")}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                            {t("admin.inventory.baseQty")}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                            {t("admin.inventory.countryPrice")}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                            {t("admin.inventory.countryQty")}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                            {t("admin.inventory.available")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {rowData.map((row, rowIndex) => {
                          const isFocused =
                            focusedCell?.rowIndex === rowIndex;
                          const isEditingPrice =
                            editingCell?.rowIndex === rowIndex &&
                            editingCell?.colIndex === 0;
                          const isEditingQuantity =
                            editingCell?.rowIndex === rowIndex &&
                            editingCell?.colIndex === 1;
                          const priceChanged = isCellChanged(rowIndex, 0);
                          const quantityChanged = isCellChanged(rowIndex, 1);
                          const availableChanged = isCellChanged(rowIndex, 2);

                          return (
                            <tr
                              key={`${row.productId}-${row.combination || "base"}`}
                              className={`hover:bg-gray-50 ${
                                isFocused ? "bg-blue-50" : ""
                              }`}
                            >
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {row.productTitle}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {row.combinationDisplay || "—"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {selectedLocation ? `${selectedLocation.currencySymbol}${row.basePrice.toFixed(2)}` : `$${row.basePrice.toFixed(2)}`}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {row.baseQuantity ?? "—"}
                              </td>
                              <td
                                className={`px-4 py-3 ${
                                  priceChanged
                                    ? "bg-yellow-100 border-2 border-yellow-400"
                                    : focusedCell?.rowIndex === rowIndex &&
                                      focusedCell?.colIndex === 0
                                    ? "bg-blue-100"
                                    : ""
                                }`}
                                onClick={() => {
                                  setFocusedCell({ rowIndex, colIndex: 0 });
                                  startEditing(rowIndex, 0);
                                  // Focus container to maintain keyboard navigation
                                  setTimeout(() => {
                                    if (inputRef.current) {
                                      inputRef.current.focus();
                                    } else {
                                      tableContainerRef.current?.focus();
                                    }
                                  }, 0);
                                }}
                              >
                                {isEditingPrice ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      ref={inputRef}
                                      type="number"
                                      step="0.01"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onBlur={() => finishEditing(true)}
                                      onKeyDown={(e) => {
                                        // Handle Enter, Escape, Tab in input
                                        if (e.key === "Enter" || e.key === "Escape" || e.key === "Tab") {
                                          handleKeyDown(e);
                                        }
                                        // For navigation keys, delegate to parent handler
                                        else if (
                                          e.key === "ArrowUp" ||
                                          e.key === "ArrowDown" ||
                                          e.key === "ArrowLeft" ||
                                          e.key === "ArrowRight" ||
                                          e.key === "Home" ||
                                          e.key === "End" ||
                                          e.key === "PageUp" ||
                                          e.key === "PageDown"
                                        ) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          finishEditing(true);
                                          // Focus container and trigger navigation
                                          tableContainerRef.current?.focus();
                                          // Create a synthetic event for the parent handler
                                          const syntheticEvent = {
                                            ...e,
                                            preventDefault: () => {},
                                            stopPropagation: () => {},
                                          } as React.KeyboardEvent;
                                          handleKeyDown(syntheticEvent);
                                        }
                                      }}
                                      className="w-full"
                                      label=""
                                    />
                                    {selectedLocation && (
                                      <span className="text-sm text-gray-600 font-medium">{selectedLocation.currencySymbol}</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-sm">
                                    {row.locationPrice !== undefined
                                      ? selectedLocation
                                        ? `${selectedLocation.currencySymbol}${row.locationPrice.toFixed(2)}`
                                        : `$${row.locationPrice.toFixed(2)}`
                                      : "—"}
                                  </span>
                                )}
                              </td>
                              <td
                                className={`px-4 py-3 ${
                                  quantityChanged
                                    ? "bg-yellow-100 border-2 border-yellow-400"
                                    : focusedCell?.rowIndex === rowIndex &&
                                      focusedCell?.colIndex === 1
                                    ? "bg-blue-100"
                                    : ""
                                }`}
                                onClick={() => {
                                  setFocusedCell({ rowIndex, colIndex: 1 });
                                  startEditing(rowIndex, 1);
                                  // Focus container to maintain keyboard navigation
                                  setTimeout(() => {
                                    if (inputRef.current) {
                                      inputRef.current.focus();
                                    } else {
                                      tableContainerRef.current?.focus();
                                    }
                                  }, 0);
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  {isEditingQuantity ? (
                                    <Input
                                      ref={inputRef}
                                      type="number"
                                      step="1"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onBlur={() => finishEditing(true)}
                                      onKeyDown={(e) => {
                                        // Handle Enter, Escape, Tab in input
                                        if (e.key === "Enter" || e.key === "Escape" || e.key === "Tab") {
                                          handleKeyDown(e);
                                        }
                                        // For navigation keys, delegate to parent handler
                                        else if (
                                          e.key === "ArrowUp" ||
                                          e.key === "ArrowDown" ||
                                          e.key === "ArrowLeft" ||
                                          e.key === "ArrowRight" ||
                                          e.key === "Home" ||
                                          e.key === "End" ||
                                          e.key === "PageUp" ||
                                          e.key === "PageDown"
                                        ) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          finishEditing(true);
                                          // Focus container and trigger navigation
                                          tableContainerRef.current?.focus();
                                          // Create a synthetic event for the parent handler
                                          const syntheticEvent = {
                                            ...e,
                                            preventDefault: () => {},
                                            stopPropagation: () => {},
                                          } as React.KeyboardEvent;
                                          handleKeyDown(syntheticEvent);
                                        }
                                      }}
                                      className="w-full"
                                      label=""
                                    />
                                  ) : (
                                    <span className="text-sm">
                                      {row.locationQuantity !== undefined
                                        ? row.locationQuantity
                                        : "—"}
                                    </span>
                                  )}
                                  {hasInconsistentInventory(rowIndex) && (
                                    <div
                                      className="flex-shrink-0"
                                      title={t("admin.inventory.inconsistentInventoryTooltip")}
                                    >
                                      <svg
                                        className="w-4 h-4 text-yellow-600"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td
                                className={`px-4 py-3 text-center ${
                                  availableChanged
                                    ? "bg-yellow-100 border-2 border-yellow-400"
                                    : focusedCell?.rowIndex === rowIndex &&
                                      focusedCell?.colIndex === 2
                                    ? "bg-blue-100"
                                    : ""
                                }`}
                                onClick={(e) => {
                                  // Don't toggle if clicking directly on checkbox
                                  if ((e.target as HTMLElement).tagName === "INPUT") {
                                    return;
                                  }
                                  setFocusedCell({ rowIndex, colIndex: 2 });
                                  const currentValue = getCellValue(rowIndex, 2) as boolean;
                                  updateChange(rowIndex, 2, !currentValue);
                                  // Focus container to maintain keyboard navigation
                                  setTimeout(() => {
                                    tableContainerRef.current?.focus();
                                  }, 0);
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={getCellValue(rowIndex, 2) as boolean}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setFocusedCell({ rowIndex, colIndex: 2 });
                                    updateChange(rowIndex, 2, e.target.checked);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const currentValue = getCellValue(rowIndex, 2) as boolean;
                                      updateChange(rowIndex, 2, !currentValue);
                                      // Move to next row after toggle
                                      const nextRowIndex = Math.min(rowIndex + 1, rowData.length - 1);
                                      setFocusedCell({ rowIndex: nextRowIndex, colIndex: 2 });
                                      setTimeout(() => {
                                        tableContainerRef.current?.focus();
                                      }, 0);
                                    } else if (
                                      e.key === "ArrowUp" ||
                                      e.key === "ArrowDown" ||
                                      e.key === "ArrowLeft" ||
                                      e.key === "ArrowRight" ||
                                      e.key === "Home" ||
                                      e.key === "End" ||
                                      e.key === "PageUp" ||
                                      e.key === "PageDown" ||
                                      e.key === "Tab"
                                    ) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      tableContainerRef.current?.focus();
                                      handleKeyDown(e);
                                    }
                                  }}
                                  className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer ${
                                    availableChanged ? "ring-2 ring-yellow-400" : ""
                                  }`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Summary Modal */}
      <Modal
        isOpen={showSummary}
        onClose={() => setShowSummary(false)}
        title={t("admin.inventory.reviewChanges")}
        size="lg"
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowSummary(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Spinner size="sm" /> : t("admin.inventory.confirmSubmit")}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t(`admin.inventory.updateSummary_${getChangesSummary.total === 1 ? "one" : "other"}`, { 
              count: getChangesSummary.total, 
              country: selectedLocation?.country || "" 
            })}
          </p>
          <div className="border border-gray-200 rounded-lg overflow-auto max-h-[400px]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">
                    {t("admin.inventory.product")}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">
                    {t("admin.inventory.variant")}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">
                    {t("admin.inventory.changes")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getChangesSummary.changes.map((change, idx) => {
                  const product = products.find((p) => p._id === change.productId);
                  const comboDisplay = change.combination
                    ? change.combination
                        .split("|")
                        .map((pair: string) => {
                          const [opt, val] = pair.split(":");
                          return `${opt}: ${val}`;
                        })
                        .join(", ")
                    : t("admin.inventory.baseProduct");

                  const changeItems: string[] = [];
                  if (change.price !== undefined) {
                    const priceDisplay = selectedLocation
                      ? `${selectedLocation.currencySymbol}${change.price.toFixed(2)}`
                      : `$${change.price.toFixed(2)}`;
                    changeItems.push(`${t("admin.inventory.price")}: ${priceDisplay}`);
                  }
                  if (change.inventoryQuantity !== undefined)
                    changeItems.push(`${t("admin.inventory.quantity")}: ${change.inventoryQuantity}`);
                  if (change.isAvailable !== undefined)
                    changeItems.push(`${t("admin.inventory.available")}: ${change.isAvailable ? t("common.yes") : t("common.no")}`);

                  return (
                    <tr key={idx}>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {product?.title || t("admin.inventory.unknown")}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">{comboDisplay}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {changeItems.join(", ")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </Container>
  );
};

